import { AppDispatch } from '@/lib/store';
import { setUnitId } from '@/lib/slices/unit-slice';
import { setSubunitId } from '@/lib/slices/subunit-slice';
import {
  setActivityDescription,
  setActivityId,
  setActivityName,
  setActivityType,
  setActivityURL,
  setActivityIndex,
} from '@/lib/slices/activity-slice';
import { setModalName, setModalVisible } from '@/lib/slices/modal-slice';
import { setCreateQuizActive, setIsEditingQuiz } from '@/lib/slices/quiz-ui-slice';
import {
  setQuizData,
  setQuizName,
  setQuizId,
  setAnswerData,
  setQuizDueDate,
  setQuizAllowedAttempts,
  setQuizLockQuiz,
  setBlocksDownstream,
  setMinimumGradeThreshold,
  setRequiresMinimumGrade,
} from '@/lib/slices/quiz-slice';
import ActiveEditCacheService from '@/services/storage/active-edit-cache-service';
import Request from '@/services/util/request-service';
import toast from 'react-hot-toast';
import { ActivityData } from 'course-types';

/**
 * Service for handling activity editing operations.
 * Centralizes the logic for opening different activity types in their respective editors.
 */
const ActivityEditService = {
  /**
   * Open editor for a quiz activity
   */
  async openQuizEditor(
    activity: ActivityData,
    unitId: string,
    subunitId: string | undefined,
    dispatch: AppDispatch,
  ): Promise<void> {
    try {
      // Fetch quiz data from API
      const response = await Request.get(`quiz/${activity.id}`);
      const data = (await response?.json())?.quiz;

      // Close out if quiz does not exist / fetch correctly
      if (!data) {
        toast.error('Error fetching quiz data. Please refresh and try again.');
        return;
      }

      // Dispatch quiz info for editor
      dispatch(setQuizId(activity.id));
      dispatch(setQuizName(activity.name));
      dispatch(setQuizData(data.quiz_data));
      dispatch(setAnswerData(data.answer_key));
      dispatch(setQuizDueDate(activity.due_date ?? null));
      dispatch(setQuizLockQuiz(data.lock_quiz ?? false));
      dispatch(setQuizAllowedAttempts(activity.attempts ?? null));
      dispatch(setActivityIndex(activity.index ?? 0));
      dispatch(setSubunitId(subunitId));
      dispatch(setUnitId(unitId));
      dispatch(setRequiresMinimumGrade(data.requires_minimum_grade));
      dispatch(setMinimumGradeThreshold(data.minimum_grade_threshold));
      dispatch(setBlocksDownstream(data.blocks_downstream_content));

      // Open quiz editor in edit mode
      dispatch(setCreateQuizActive(true));
      dispatch(setIsEditingQuiz(true));
    } catch (error) {
      console.error('Error fetching quiz data:', error);
      toast.error('Error fetching quiz data. Please refresh and try again.');
    }
  },

  /**
   * Open editor for a modifiable document (has last_edited date)
   */
  openModifiableDocumentEditor(
    activity: ActivityData,
    unitId: string,
    subunitId: string | undefined,
    dispatch: AppDispatch,
    router: any,
  ): void {
    // For modifiable documents, set up Redux state, cache and redirect to assistiv docs page
    dispatch(setUnitId(unitId));
    dispatch(setSubunitId(subunitId));
    dispatch(setActivityIndex(activity.index ?? 0));
    dispatch(setActivityId(activity.id));
    dispatch(setActivityName(activity.name));
    dispatch(setActivityDescription(activity.description || ''));
    dispatch(setActivityType('document'));
    dispatch(setActivityURL(activity.url || ''));

    ActiveEditCacheService.setCurrentEditCookie({
      docId: activity.id,
      subunitId: subunitId,
      unitId: unitId,
      docName: activity.name,
      docDesc: activity.description || '',
      docIndex: activity.index ?? 0,
    });

    const params = new URLSearchParams();
    params.set('id', activity.id);
    router.push(`/admin/courses/assistiv-docs/edit?${params.toString()}`);
  },

  /**
   * Open content modal for uploaded documents, videos, and resources
   */
  openContentModal(
    activity: ActivityData,
    unitId: string,
    subunitId: string | undefined,
    dispatch: AppDispatch,
  ): void {
    dispatch(setUnitId(unitId));
    dispatch(setSubunitId(subunitId));
    dispatch(setActivityIndex(activity.index ?? 0));
    dispatch(setActivityId(activity.id));
    dispatch(setModalVisible(true));
    dispatch(setModalName('AddNewContent'));
  },

  /**
   * Main entry point for editing any activity type
   */
  openActivityEditor(
    activity: ActivityData,
    unitId: string,
    subunitId: string | undefined,
    dispatch: AppDispatch,
    router: any,
  ): void {
    switch (activity.type) {
      case 'quiz':
        // For quizzes, fetch quiz data and open quiz editor
        this.openQuizEditor(activity, unitId, subunitId, dispatch);
        break;

      case 'document':
        // Check if document is modifiable (has last_edited date)
        if (activity.last_edited) {
          this.openModifiableDocumentEditor(activity, unitId, subunitId, dispatch, router);
        } else {
          // For uploaded/non-modifiable documents, open content modal
          this.openContentModal(activity, unitId, subunitId, dispatch);
        }
        break;

      case 'video':
      case 'resource':
      case 'assignment':
        // For other content types, open content creator modal
        this.openContentModal(activity, unitId, subunitId, dispatch);
        break;

      default:
        console.warn('Unknown activity type:', activity.type);
    }
  },
};

export default ActivityEditService;
